/**
 * Marketplace client for fetching and caching marketplace.json
 * Implements Contract 3 validation and client-side caching
 */

import type { MarketplaceCatalog } from "@contracts/contract-3-marketplace-json";
import type { MarketplaceClientConfig, MarketplaceClientError, CatalogCache } from "../types/index";
import { env } from "./env";

const CACHE_VERSION = "1.3";
const CACHE_KEY = `skills_marketplace_catalog_v${CACHE_VERSION}`;
const DEFAULT_CACHE_EXPIRY_MS = 60000; // 60 seconds

export class MarketplaceClient {
  private config: MarketplaceClientConfig;

  constructor(config: MarketplaceClientConfig) {
    this.config = {
      cacheExpiryMs: DEFAULT_CACHE_EXPIRY_MS,
      ...config,
    };

    if (!config.catalogUrl) {
      throw new Error(
        "MarketplaceClient requires catalogUrl in config. " +
        "Provide REACT_APP_CATALOG_URL environment variable."
      );
    }
  }

  /**
   * Fetch marketplace catalog from CloudFront
   * Attempts to use cached version if available and not expired
   */
  async getCatalog(): Promise<MarketplaceCatalog> {
    // Try cache first
    const cached = this.readCache();
    if (cached) {
      return cached.catalog;
    }

    // Fetch from remote
    const catalog = await this.fetchCatalog();

    // Cache the result
    this.writeCache(catalog);

    return catalog;
  }

  /**
   * Force refresh: Bypass cache and fetch from remote
   */
  async refreshCatalog(): Promise<MarketplaceCatalog> {
    this.clearCache();
    return this.getCatalog();
  }

  /**
   * Internal: Fetch from remote URL
   */
  private async fetchCatalog(): Promise<MarketplaceCatalog> {
    let response: Response;
    try {
      response = await fetch(this.config.catalogUrl, {
        method: "GET",
        cache: "no-cache",
        headers: {
          "Accept": "application/json",
        },
      });
    } catch (error) {
      throw this.createError(
        "network",
        `Network error fetching marketplace catalog: ${error instanceof Error ? error.message : String(error)}`
      );
    }

    if (!response.ok) {
      throw this.createError(
        "network",
        `Failed to fetch marketplace.json: ${response.status} ${response.statusText}`
      );
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch (error) {
      throw this.createError(
        "parse",
        `Failed to parse marketplace.json as JSON: ${error instanceof Error ? error.message : String(error)}`
      );
    }

    return this.validateCatalog(json);
  }

  /**
   * Internal: Validate catalog against Contract 3 schema
   */
  private validateCatalog(json: unknown): MarketplaceCatalog {
    if (!json || typeof json !== "object") {
      throw this.createError("parse", "Catalog is not a valid JSON object");
    }

    const catalog = json as Record<string, unknown>;

    // Validate required fields
    if (!catalog.schemaVersion) {
      throw this.createError(
        "validation",
        "Catalog missing schemaVersion field"
      );
    }

    const majorVersion = String(catalog.schemaVersion).split(".")[0];
    if (majorVersion !== "1") {
      throw this.createError(
        "validation",
        `Unsupported schema major version: ${majorVersion}. Expected 1`
      );
    }

    if (typeof catalog.totalPlugins !== "number") {
      throw this.createError(
        "validation",
        "Catalog missing or invalid totalPlugins field"
      );
    }

    if (!Array.isArray(catalog.plugins)) {
      throw this.createError(
        "validation",
        "Catalog missing or invalid plugins field (expected array)"
      );
    }

    // Validate plugin structure: required string fields and keywords array
    const requiredStringFields = ["displayName", "pluginName", "description", "version", "source", "orgOrOwner", "repoName", "repoUrl", "lastCommitSha", "lastPushedBy", "lastUpdatedAt", "firstDiscoveredAt"];
    for (let i = 0; i < catalog.plugins.length; i++) {
      const plugin = catalog.plugins[i] as Record<string, unknown>;
      for (const field of requiredStringFields) {
        if (typeof plugin[field] !== "string") {
          throw this.createError(
            "validation",
            `Plugin at index ${i}: field "${field}" must be a string, got ${typeof plugin[field]}`
          );
        }
      }
      if (!Array.isArray(plugin.keywords)) {
        throw this.createError(
          "validation",
          `Plugin at index ${i}: field "keywords" must be an array`
        );
      }
      if (plugin.skills !== undefined && plugin.skills !== null) {
        if (!Array.isArray(plugin.skills)) {
          plugin.skills = undefined;
        } else {
          plugin.skills = (plugin.skills as unknown[]).filter(
            (s): s is { name: string; description?: string } =>
              typeof s === "object" && s !== null && typeof (s as Record<string, unknown>).name === "string" && (s as Record<string, unknown>).name !== "",
          );
          if ((plugin.skills as unknown[]).length === 0) plugin.skills = undefined;
        }
      }

      // Derive extensionType for pre-schema-1.3 records where the field is absent.
      // Mirrors the catalog-builder's deriveExtensionType logic so all components
      // can rely on extensionType being present without individual fallbacks.
      if (plugin.extensionType !== "plugin" && plugin.extensionType !== "skill") {
        const skills = Array.isArray(plugin.skills) ? plugin.skills as Array<{ name: string }> : [];
        const pluginName = (plugin.pluginName as string ?? "").toLowerCase();
        // A record bundling components beyond a single skill (agents, scripts, MCP
        // servers, hooks, etc.) is a real plugin even when its sole skill shares the
        // plugin name. Mirrors catalog-builder's hasPluginComponents check.
        const hasComponents = ["mcpServers", "hooks", "agents", "scripts", "monitors", "outputStyles", "themes", "settings", "dependencies"]
          .some((k) => Array.isArray(plugin[k]) && (plugin[k] as unknown[]).length > 0);
        if (skills.length === 1 && skills[0].name?.toLowerCase() === pluginName && !hasComponents) {
          plugin.extensionType = "skill";
        } else if (skills.length > 0) {
          plugin.extensionType = "plugin";
        } else {
          // No skills[] and no skillPath: nothing structural distinguishes a
          // standalone single-skill repo from a bare plugin, so fall back to
          // claude-chat membership as a heuristic. Mirrors catalog-builder's
          // deriveExtensionType. (Chat now runs plugins too, so a bare
          // chat-compatible plugin can be mislabeled "skill" — known limitation.)
          const clients = Array.isArray(plugin.supportedClients) ? plugin.supportedClients as string[] : [];
          plugin.extensionType = clients.includes("claude-chat") || clients.includes("claude-desktop")
            ? "skill"
            : "plugin";
        }
      }
    }

    return catalog as unknown as MarketplaceCatalog;
  }

  /**
   * Internal: Read from localStorage cache
   */
  private readCache(): CatalogCache | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const data = JSON.parse(cached) as CatalogCache;
      const age = Date.now() - data.retrievedAt;

      // Check if cache is still valid
      if (age < (this.config.cacheExpiryMs || DEFAULT_CACHE_EXPIRY_MS)) {
        return data;
      }

      // Cache expired
      this.clearCache();
      return null;
    } catch (error) {
      console.warn("Failed to read cache:", error);
      this.clearCache();
      return null;
    }
  }

  /**
   * Internal: Write to localStorage cache
   */
  private writeCache(catalog: MarketplaceCatalog): void {
    try {
      const data: CatalogCache = {
        catalog,
        retrievedAt: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn("Failed to write cache:", error);
    }
  }

  /**
   * Internal: Clear cache
   */
  private clearCache(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.warn("Failed to clear cache:", error);
    }
  }

  /**
   * Internal: Create an error object
   */
  private createError(type: MarketplaceClientError["type"], message: string): MarketplaceClientErrorImpl {
    return new MarketplaceClientErrorImpl(type, message);
  }
}

/**
 * Custom error class for marketplace client errors
 */
export class MarketplaceClientErrorImpl extends Error implements MarketplaceClientError {
  type: MarketplaceClientError["type"];
  message: string;
  details?: Record<string, unknown>;

  constructor(type: MarketplaceClientError["type"], message: string, details?: Record<string, unknown>) {
    super(message);
    this.type = type;
    this.message = message;
    this.details = details;
    this.name = "MarketplaceClientError";
  }
}

/**
 * Create a singleton instance of MarketplaceClient
 */
let clientInstance: MarketplaceClient | null = null;

export function createMarketplaceClient(config?: Partial<MarketplaceClientConfig>): MarketplaceClient {
  const catalogUrl = env.REACT_APP_CATALOG_URL || "/marketplace.json";
  const fullConfig: MarketplaceClientConfig = {
    catalogUrl,
    ...config,
  };
  return new MarketplaceClient(fullConfig);
}

export function getMarketplaceClient(): MarketplaceClient {
  if (!clientInstance) {
    clientInstance = createMarketplaceClient();
  }
  return clientInstance;
}
